// ============================================================
// CLUBHUB – Pinnwand: die Ansicht in der Adresse
// ============================================================
import { TaskStatus } from "@/generated/prisma/enums";
import { readSort, type SortColumns, type SortState } from "@/lib/sorting";

type Frage = Record<string, string | string[] | undefined>;

function eins(p: Frage, name: string): string {
  const w = p[name];
  return ((Array.isArray(w) ? w[0] : w) ?? "").trim();
}

// ── Aushaenge (/pinnwand) ───────────────────────────────────

export const ZIELE = [
  { schluessel: "alle", label: "Alle Mitglieder", audience: "ALL" },
  { schluessel: "vorstand", label: "Vorstand", audience: "BOARD" },
  { schluessel: "gruppe", label: "Gruppen", audience: "GROUP" },
] as const;
export type Ziel = (typeof ZIELE)[number]["schluessel"];

export type AushangAnsicht = {
  q: string;
  ziel: Ziel | null;
  /** Eine bestimmte Gruppe (setzt `ziel=gruppe` voraus). */
  gruppe: string;
  abgelaufen: boolean;
};

export const AUSHANG_PARAMS = ["q", "ziel", "gruppe", "zustand"] as const;

export function aushangAnsichtLesen(p: Frage): AushangAnsicht {
  const ziel = eins(p, "ziel");
  return {
    q: eins(p, "q"),
    ziel: ZIELE.some((z) => z.schluessel === ziel) ? (ziel as Ziel) : null,
    gruppe: eins(p, "gruppe"),
    abgelaufen: eins(p, "zustand") === "abgelaufen",
  };
}

export function aushangFilterZahl(a: AushangAnsicht): number {
  // Eine Gruppe setzt `ziel=gruppe` mit — ein Klick, ein Filter.
  return [a.q, a.ziel || a.gruppe, a.abgelaufen].filter(Boolean).length;
}

// ── Aufgaben (/pinnwand/aufgaben) ───────────────────────────

export const FAELLIGKEITEN = [
  { schluessel: "ueberfaellig", label: "Überfällig", hinweis: "Fälligkeit vorbei, noch nicht erledigt" },
  { schluessel: "woche", label: "Nächste 7 Tage", hinweis: "Heute bis in einer Woche fällig" },
  { schluessel: "ohne", label: "Ohne Datum", hinweis: "Noch keine Fälligkeit gesetzt" },
] as const;
export type Faelligkeit = (typeof FAELLIGKEITEN)[number]["schluessel"];

export const AUFGABEN_SORT = {
  aufgabe: { label: "Aufgabe", first: "auf" },
  zustaendig: { label: "Zuständig", first: "auf" },
  faellig: { label: "Fällig", first: "auf" },
  status: { label: "Status", first: "auf" },
} as const satisfies SortColumns<string>;
export type AufgabenSortKey = keyof typeof AUFGABEN_SORT;
export const AUFGABEN_DEFAULT_SORT: AufgabenSortKey = "faellig";

export type AufgabenAnsicht = {
  q: string;
  darstellung: "brett" | "liste";
  faellig: Faelligkeit | null;
  /** Nur in der Liste — im Brett sind die Zustaende die Spalten. */
  status: TaskStatus | null;
  personen: string[];
  /** Ohne Zustaendige. */
  offenFuerAlle: boolean;
  sort: SortState<AufgabenSortKey>;
};

/** Was „Filter zurücksetzen“ leert — die Darstellung nicht. */
export const AUFGABEN_PARAMS = ["q", "faellig", "status", "person", "frei", "sortierung", "richtung"] as const;

const IST_STATUS = new Set<string>(Object.values(TaskStatus));

export function aufgabenAnsichtLesen(p: Frage): AufgabenAnsicht {
  const faellig = eins(p, "faellig");
  const status = eins(p, "status").toUpperCase();
  return {
    q: eins(p, "q"),
    darstellung: eins(p, "darstellung") === "liste" ? "liste" : "brett",
    faellig: FAELLIGKEITEN.some((f) => f.schluessel === faellig) ? (faellig as Faelligkeit) : null,
    status: IST_STATUS.has(status) ? (status as TaskStatus) : null,
    personen: eins(p, "person").split(",").map((s) => s.trim()).filter(Boolean),
    offenFuerAlle: eins(p, "frei") === "1",
    sort: readSort(p, AUFGABEN_SORT, AUFGABEN_DEFAULT_SORT),
  };
}

export function aufgabenFilterZahl(a: AufgabenAnsicht): number {
  return [a.q, a.faellig, a.status && a.darstellung === "liste", a.personen.length > 0, a.offenFuerAlle].filter(Boolean).length;
}
